/* eslint-disable no-script-url */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Box, IconButton } from "@mui/material";
import { useNavigate } from "react-router-dom";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MembershipStatusModal from "../../Modals/membershipStatusModal";
import RejectReasonModal from "../../Modals/rejectReason";
import CustomPagination from "../../components/CustomPagination";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import dayjs, { Dayjs } from "dayjs";
import {
  useApproveMemberMutation,
  useCancelMemberMutation,
  useLazyDeleteUserQuery,
} from "../../service/user";
import { showError, showToast } from "../../constants/toasts";
import StarIcon from "@mui/icons-material/Star";
import { formatDubaiDateTime } from "../../utils/dateTime";
import { useLazyGetSubscriptionQuery } from "../../service/subscription";
import { ConfirmationModal } from "../../Modals/confirmationModal";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { MEMBER_SORTING } from "../../utils/enums";

const CustomerListPending = ({
  data,
  setDebouncedSearchTerm,
  debouncedSearchTerm,
  getUsersApi,
  hasFetched,
  count,
  page,
  setPage,
  sortOrder,
  setSortOrder,
}: {
  data: any;
  setDebouncedSearchTerm: Dispatch<SetStateAction<string>>;
  debouncedSearchTerm: string;
  getUsersApi: () => void;
  hasFetched: boolean;
  count: number;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  sortOrder: string;
  setSortOrder: Dispatch<SetStateAction<string>>;
}) => {
  const navigate = useNavigate();
  const [getSubscription] = useLazyGetSubscriptionQuery();
  const [deleteApi] = useLazyDeleteUserQuery();

  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [approveMember] = useApproveMemberMutation();
  const [rejectMember] = useCancelMemberMutation();
  const [membershipStatus, setMembershipStatus] = useState("Trial");
  const [selectedId, setSelectedId] = useState("");
  const [trialDate, setTrialDate] = useState<Dayjs | null>(null);
  const [compDate, setCompDate] = useState<Dayjs | null>(null);
  const [memberType, setMemberType] = useState("");
  const [reason, setReason] = useState("");
  const [selectedShortcut, setSelectedShortcut] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<any>([]);
  const [selectedSubscription, setSelectedSubscription] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [validFor, setValidFor] = useState<string>("");
  const [updateOption, setUpdateOption] = useState("no");
  const [deleteOpen, setDeleteOpen] = useState(false);

  const handleShortcut = (duration: any, membershipStatus: string) => {
    let newDate = dayjs();
    switch (duration) {
      case "1w":
        newDate = newDate?.add(1, "week");
        break;
      case "1m":
        newDate = newDate?.add(1, "month");
        break;
      case "3m":
        newDate = newDate?.add(3, "month");
        break;
      case "6m":
        newDate = newDate?.add(6, "month");
        break;
      case "1y":
        newDate = newDate?.add(1, "year");
        break;
      case "10y":
        newDate = newDate?.add(10, "year");
        break;
      default:
        break;
    }
    if (membershipStatus === "Trial") {
      setTrialDate(newDate);
    } else {
      setCompDate(newDate);
    }
  };

  const approveMemberApi = async () => {
    setLoading(true);

    if (!membershipStatus) {
      showError("Please select membership status");
      return;
    } else if (membershipStatus === "Trial" && !trialDate) {
      showError("Please select expiry date");
      return;
    } else if (membershipStatus === "Complementary" && !compDate) {
      showError("Please select expiry date");
      return;
    }

    if (membershipStatus === "Paid" && updateOption === "yes") {
      if (!selectedSubscription) {
        showError("Please select subscription");
        return;
      } else if (!amount) {
        showError("Please enter amount");
        return;
      } else if (!validFor) {
        showError("Please enter duration valid for");
        return;
      }
    }

    try {
      const body = {
        memberShip:
          membershipStatus === "Complementary"
            ? "Complimentary"
            : membershipStatus,
        userId: selectedId,
        endDate:
          membershipStatus === "Trial"
            ? trialDate?.toISOString()
            : compDate?.toISOString(),
        ...(updateOption === "yes"
          ? {
              assignMemberShip: {
                subscriptionSpecificAmount: amount,
                subscriptionId: selectedSubscription,
                validCycle: validFor,
              },
            }
          : {}),
      };

      const response = await approveMember(body).unwrap();
      setLoading(false);
      if (response.status === 200) {
        getUsersApi();
        setOpen1(false);
        showToast("Member approved successfully");
        setTrialDate(null);
        setCompDate(null);
        setMembershipStatus("Trial");
        setAmount("");
        setValidFor("");
        setSelectedSubscription("");
        setUpdateOption("no");
      } else {
        showError(response?.message || "");
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const rejectMemberApi = async () => {
    setLoading(true);

    if (!reason) {
      showError("Please enter reason");
      return;
    }

    try {
      const body = {
        userId: selectedId,
        reason: reason,
      };

      const response = await rejectMember(body).unwrap();
      setLoading(false);
      if (response.status === 200) {
        getUsersApi();
        setOpen(false);
        showToast("Member rejected successfully");
      } else {
        showError(response?.message || "");
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const deleteUserApi = async (id: string) => {
    try {
      const response = await deleteApi({
        id,
      }).unwrap();
      if (response.status === 200) {
        getUsersApi();
        setDeleteOpen(false);
        showToast(response?.message);
      } else {
        showError(response?.message);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
    }
  };

  const toggleExpand = (id: number) => {
    setExpandedRowId((prevId) => (prevId === id ? null : id));
  };

  const [open, setOpen] = useState(false);
  const handleCloseModal = () => {
    setOpen(false);
  };

  const [open1, setOpen1] = useState(false);
  const handleCloseModal1 = () => {
    setOpen1(false);
  };

  const getSubscriptionApi = async () => {
    setLoading(true);
    try {
      const response = await getSubscription({
        search: debouncedSearchTerm.trim(),
        page,
      }).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setSubscription(response?.data?.items || []);
      } else {
        setSubscription([]);
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    if (membershipStatus === "Paid") {
      getSubscriptionApi();
    }
  }, [membershipStatus]);

  return (
    <>
      <div className="sorting_table cards table_cards">
        <div className="table_container">
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "fixed",
            }}
          >
            <thead>
              <tr>
                <th>
                  <div
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Name
                    <SwapVertIcon
                      onClick={() =>
                        setSortOrder(
                          sortOrder === MEMBER_SORTING.Name_asc
                            ? "Name_desc"
                            : "Name_asc"
                        )
                      }
                    />
                  </div>
                </th>
                <th className="email_data">
                  <div
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Email
                    <SwapVertIcon
                      onClick={() =>
                        setSortOrder(
                          sortOrder === MEMBER_SORTING.Email_asc
                            ? "Email_desc"
                            : "Email_asc"
                        )
                      }
                    />
                  </div>
                </th>
                <th>
                  <div
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Membership Type
                    <SwapVertIcon
                      onClick={() =>
                        setSortOrder(
                          sortOrder === MEMBER_SORTING.MembershipType_asc
                            ? "MembershipType_desc"
                            : "MembershipType_asc"
                        )
                      }
                    />
                  </div>
                </th>
                <th>
                  <div
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    Created Date
                    <SwapVertIcon
                      onClick={() =>
                        setSortOrder(
                          sortOrder === MEMBER_SORTING.DateCreated_asc
                            ? "DateCreated_desc"
                            : "DateCreated_asc"
                        )
                      }
                    />
                  </div>
                </th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data?.length ? (
                data?.map((row: any) => (
                  <React.Fragment key={row.id}>
                    <tr>
                      <td>
                        <div
                          role="button"
                          tabIndex={0}
                          className="user_block click_action"
                          onClick={() =>
                            navigate(`/manage-customers/detail/${row?.id}`, {
                              state: {
                                status: "PENDING",
                                memberType: row?.membershipType,
                              },
                            })
                          }
                        >
                          {row?.isVIP ? <StarIcon /> : null}

                          <figure>
                            <img
                              src="/static/images/user_placeholder.png"
                              alt=""
                            />
                          </figure>
                          <p>
                            <b>{row?.name || "-"}</b>
                          </p>
                        </div>
                      </td>
                      <td className="email_data">{row.email}</td>
                      <td>
                        {row?.membershipType}
                        {row?.membershipType === "Duo" && (
                          <IconButton
                            className="d_active"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevents triggering parent navigation if added
                              toggleExpand(row.id);
                            }}
                            size="small"
                            style={{ verticalAlign: "middle", marginLeft: 4 }}
                          >
                            {expandedRowId === row.id ? (
                              <ArrowDropDownIcon
                                style={{ transform: "rotate(180deg)" }}
                              />
                            ) : (
                              <ArrowDropDownIcon />
                            )}
                          </IconButton>
                        )}
                      </td>
                      <td>
                        {row?.createdDate
                          ? formatDubaiDateTime(row?.createdDate)
                          : "-"}
                      </td>
                      <td>
                        <div className="table_actions v2">
                          <Box>
                            <IconButton
                              className="cancel_icon"
                              onClick={() => {
                                setSelectedId(row?.id);
                                setOpen(true);
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Box>
                          <Box>
                            <IconButton
                              className="accept_icon"
                              onClick={() => {
                                setSelectedId(row?.id);
                                setOpen1(true);
                                setMemberType(row?.membershipType);
                              }}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Box>
                          <Box>
                            <IconButton
                              onClick={() => {
                                setDeleteOpen(true);
                                setSelectedId(row?.id);
                              }}
                            >
                              <img
                                src="/static/images/trash2_icon.svg"
                                alt="Delete"
                              />
                            </IconButton>
                          </Box>
                        </div>
                      </td>
                    </tr>

                    {expandedRowId === row?.id && (
                      <tr>
                        <td colSpan={5}>
                          <div className="expanded-content p_inline_0">
                            <table
                              style={{
                                width: "100%",
                                borderCollapse: "collapse",
                                tableLayout: "fixed",
                              }}
                            >
                              <tbody>
                                <tr>
                                  <td>
                                    <div
                                      role="button"
                                      tabIndex={0}
                                      className="user_block click_action"
                                      onClick={() => {
                                        if (row?.partner) {
                                          navigate(
                                            `/manage-customers/detail/${row?.partner?.id}`,
                                            {
                                              state: {
                                                status: "PENDING",
                                                memberType: row?.membershipType,
                                                partner: true,
                                              },
                                            }
                                          );
                                        }
                                      }}
                                    >
                                      {row?.isVIP ? <StarIcon /> : null}
                                      <figure>
                                        <img
                                          src="/static/images/user_placeholder.png"
                                          alt=""
                                        />
                                      </figure>
                                      <p>
                                        <b>{row?.partner?.name || "-"}</b>
                                      </p>
                                    </div>
                                  </td>
                                  <td className="email_data">
                                    {row?.partner?.email || "-"}
                                  </td>
                                  <td>{row?.partner?.membershipType || "-"}</td>
                                  <td>
                                    {row?.partner?.createdDate
                                      ? formatDubaiDateTime(
                                          row?.partner?.createdDate
                                        )
                                      : "-"}
                                  </td>
                                  <td>
                                    <div className="table_actions v2">
                                      {/* <Box>
                                        <IconButton
                                          className="cancel_icon"
                                          onClick={() => setOpen(true)}
                                        >
                                          <CancelIcon />
                                        </IconButton>
                                      </Box>
                                      <Box>
                                        <IconButton
                                          className="accept_icon"
                                          onClick={() => {
                                            setOpen1(true);
                                          }}
                                        >
                                          <CheckCircleIcon />
                                        </IconButton>
                                      </Box> */}
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              ) : hasFetched ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    No Data Found
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
      {data?.length ? (
        <CustomPagination
          currentPage={page - 1}
          totalPages={Math.ceil(count / 10)}
          count={count}
          pageSize={10}
          onPageChange={(page) => setPage(page + 1)}
        />
      ) : null}

      <RejectReasonModal
        open={open}
        onClose={handleCloseModal}
        setOpen={setOpen}
        onClick={() => {
          rejectMemberApi();
          setOpen(false);
        }}
        setReason={setReason}
        reason={reason}
      />
      <MembershipStatusModal
        open={open1}
        onClose={handleCloseModal1}
        setOpen={setOpen1}
        setMembershipStatus={setMembershipStatus}
        membershipStatus={membershipStatus}
        setTrialDate={setTrialDate}
        trialDate={trialDate}
        setCompDate={setCompDate}
        compDate={compDate}
        onClick={approveMemberApi}
        memberType={memberType}
        handleShortcut={handleShortcut}
        setSelectedShortcut={setSelectedShortcut}
        selectedShortcut={selectedShortcut}
        subscription={subscription}
        setSelectedSubscription={setSelectedSubscription}
        selectedSubscription={selectedSubscription}
        amount={amount}
        setAmount={setAmount}
        validFor={validFor}
        setValidFor={setValidFor}
        updateOption={updateOption}
        setUpdateOption={setUpdateOption}
      />

      <ConfirmationModal
        text={"Are you sure to delete this Member?"}
        openModal={deleteOpen}
        setOpenModal={setDeleteOpen}
        handleSubmit={() => {
          deleteUserApi(selectedId);
        }}
      />
    </>
  );
};

export default CustomerListPending;
